import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Paper, Stack, Typography } from '@rossum/ui/material';
import { getRoleName } from '../../../redux/modules/groups/helpers';
import { aiFeaturesEnabledSelector } from '../../../redux/modules/organizationGroup/selectors';
import { State } from '../../../types/state';
import { User } from '../../../types/user';
import { FeatureToggles } from '../../feature-enablement-ui/FeatureToggles';
import {
  isFeatureEnabled,
  isFeatureGloballyEnabled,
  isFeatureLocallyEnabled,
} from '../../feature-enablement/featureEnablement';
import {
  Feature,
  labFeatures,
  labFeaturesList,
} from '../../feature-enablement/features';
import { approvalWorkflowsFeatureSelector } from '../../pricing/selectors';

export const useVisibleLabFeatures = (user: User) => {
  const groups = useSelector((state: State) => state.groups);
  const role = useMemo(
    () => getRoleName(user.groups, groups),
    [user.groups, groups]
  );

  const isApprovalWorkflowsEnabled = useSelector(
    approvalWorkflowsFeatureSelector
  );

  const aiFeaturesEnabled = useSelector(aiFeaturesEnabledSelector);

  const visibleLabFeatures = useMemo(() => {
    return labFeaturesList.filter(
      feature =>
        // You see improved requests dashboard only if you have approval workflows
        (feature.id === 'pat-improved-requests-dashboard'
          ? isApprovalWorkflowsEnabled
          : true) &&
        // Rossum Agent is an AI feature
        (feature.id === 'mr-fabry' ? aiFeaturesEnabled : true) &&
        // missing `groups` means that any role can see the flag
        (!feature.groups ||
          feature.groups.find(groupRole => groupRole === role)) &&
        (isFeatureLocallyEnabled(feature.id) ||
          isFeatureGloballyEnabled(feature.id))
    );
  }, [role, isApprovalWorkflowsEnabled, aiFeaturesEnabled]);

  return visibleLabFeatures;
};

export const RossumLabsPage = ({
  visibleLabFeatures,
  user,
}: {
  visibleLabFeatures: Feature[];
  user: User;
}) => {
  const intl = useIntl();

  const userLabConfig = useMemo(
    () => user.uiSettings?.labConfig || {},
    [user.uiSettings?.labConfig]
  );

  const featureConfig = useMemo(() => {
    return R.mapValues(labFeatures, feature => ({
      enabled: isFeatureEnabled(feature.id, userLabConfig),
      askForFeedback: !userLabConfig[feature.id]?.sentFeedback,
    }));
  }, [userLabConfig]);

  return (
    <Paper>
      <Stack>
        <Stack sx={{ p: 4 }}>
          <Typography variant="h5">
            {intl.formatMessage({
              id: 'features.personalSettings.rossumLabs',
            })}
          </Typography>
        </Stack>
        <FeatureToggles
          features={visibleLabFeatures}
          config={featureConfig}
          collectFeedback
        />
      </Stack>
    </Paper>
  );
};
